import { ApiOutlined, CloudServerOutlined, DatabaseOutlined, UploadOutlined } from '@ant-design/icons';

// Display names (used in UI)
export type SourceTypeDisplay =
    | 'Epic'
    | 'S3'
    | 'Snowflake'
    | 'Local Upload'
    | 'HTTP Plugin'
    | 'SFTP Connector'
    | 'AWS HealthLake'
    | 'EHR'
    | 'AWS HealthImaging'
    | 'DICOM-web';

// Backend API names (used in API calls)
export type SourceTypeBackend =
    | 'epic'
    | 's3'
    | 'snowflake'
    | 'local_fs'
    | 'http'
    | 'sftp'
    | 'healthlake'
    | 'ehr'
    | 'aws-healthimaging'
    | 'dicomweb';

// Icon names (used by Icon component)
export type SourceIconName =
    | 'Epic'
    | 'S3'
    | 'SnowFlake'
    | 'UploadOutlined'
    | 'ApiOutlined'
    | 'CloudServerOutlined'
    | 'DatabaseOutlined'
    | 'HealthLake'
    | 'AwsHealthImaging'
    | 'DicomWeb';

/**
 * Source type configuration
 * Each source has:
 * - display: Display name shown in UI
 * - backend: Backend API identifier
 * - icon: Icon configuration (custom or Ant Design)
 * - supportsConnectionTest: Whether this source supports connection testing before creation
 */
export const SOURCE_TYPES = {
    EPIC: {
        display: 'Epic' as SourceTypeDisplay,
        backend: 'epic' as SourceTypeBackend,
        icon: { type: 'custom' as const, name: 'Epic' as const, size: 44 },
        supportsConnectionTest: false,
    },
    S3: {
        display: 'S3' as SourceTypeDisplay,
        backend: 's3' as SourceTypeBackend,
        icon: { type: 'custom' as const, name: 'S3' as const, size: 44 },
        supportsConnectionTest: true,
    },
    SNOWFLAKE: {
        display: 'Snowflake' as SourceTypeDisplay,
        backend: 'snowflake' as SourceTypeBackend,
        icon: { type: 'custom' as const, name: 'SnowFlake' as const, size: 52 },
        supportsConnectionTest: true,
    },
    HTTP: {
        display: 'HTTP Plugin' as SourceTypeDisplay,
        backend: 'http' as SourceTypeBackend,
        icon: { type: 'antd' as const, name: 'ApiOutlined' as const, component: ApiOutlined, size: 44 },
        supportsConnectionTest: true,
    },
    LOCAL_FS: {
        display: 'Local Upload' as SourceTypeDisplay,
        backend: 'local_fs' as SourceTypeBackend,
        icon: { type: 'antd' as const, name: 'UploadOutlined' as const, component: UploadOutlined, size: 44 },
        supportsConnectionTest: false,
    },
    SFTP: {
        display: 'SFTP Connector' as SourceTypeDisplay,
        backend: 'sftp' as SourceTypeBackend,
        icon: { type: 'antd' as const, name: 'CloudServerOutlined' as const, component: CloudServerOutlined, size: 44 },
        supportsConnectionTest: true,
    },
    HEALTHLAKE: {
        display: 'AWS HealthLake' as SourceTypeDisplay,
        backend: 'healthlake' as SourceTypeBackend,
        icon: { type: 'custom' as const, name: 'HealthLake' as const, size: 44 },
        supportsConnectionTest: true,
    },
    EHR: {
        display: 'EHR' as SourceTypeDisplay,
        backend: 'ehr' as SourceTypeBackend,
        icon: { type: 'antd' as const, name: 'DatabaseOutlined' as const, component: DatabaseOutlined, size: 44 },
        supportsConnectionTest: true,
    },
    AWS_HEALTHIMAGING: {
        display: 'AWS HealthImaging' as SourceTypeDisplay,
        backend: 'aws-healthimaging' as SourceTypeBackend,
        icon: { type: 'custom' as const, name: 'AwsHealthImaging' as const, size: 44 },
        supportsConnectionTest: false,
    },
    DICOMWEB: {
        display: 'DICOM-web' as SourceTypeDisplay,
        backend: 'dicomweb' as SourceTypeBackend,
        icon: {
            type: 'img' as const,
            name: 'DicomWeb' as const,
            component: () => (
                <img
                    src="/images/dicom-logo.jpg"
                    alt="DICOM-web"
                />
            ),
            size: 44,
        },
        supportsConnectionTest: false,
    },
} as const;

const displayToBackendMap = Object.values(SOURCE_TYPES).reduce(
    (acc, type) => {
        acc[type.display] = type.backend;
        return acc;
    },
    {} as Record<string, SourceTypeBackend>
);

const backendToDisplayMap = Object.values(SOURCE_TYPES).reduce(
    (acc, type) => {
        acc[type.backend] = type.display;
        return acc;
    },
    {} as Record<string, SourceTypeDisplay>
);

const displayToIconMap = Object.values(SOURCE_TYPES).reduce(
    (acc, type) => {
        acc[type.display] = type.icon;
        return acc;
    },
    {} as Record<string, (typeof SOURCE_TYPES)[keyof typeof SOURCE_TYPES]['icon']>
);

export function mapDisplayToBackend(displayType: string): SourceTypeBackend {
    return displayToBackendMap[displayType] || (displayType.toLowerCase() as SourceTypeBackend);
}

export function mapBackendToDisplay(backendType: string): SourceTypeDisplay {
    return backendToDisplayMap[backendType] || (backendType as SourceTypeDisplay);
}

export function getIconConfig(displayType: string) {
    return displayToIconMap[displayType] || { type: 'custom' as const, name: 'DataBaseFilled' as const, size: 44 };
}

export function getAllSourceTypeDisplays(): SourceTypeDisplay[] {
    return Object.values(SOURCE_TYPES).map(type => type.display);
}

export function getAllSourceTypeBackends(): SourceTypeBackend[] {
    return Object.values(SOURCE_TYPES).map(type => type.backend);
}

/**
 * Source type availability status
 * - 'available': Fully supported and can be used
 * - 'coming_soon': UI is available for preview but backend doesn't support it yet
 */
export type SourceTypeStatus = 'available' | 'coming_soon';

export const SOURCE_TYPE_STATUS: Record<SourceTypeBackend, SourceTypeStatus> = {
    s3: 'available',
    local_fs: 'available',
    http: 'available',
    sftp: 'available',
    healthlake: 'available',
    ehr: 'available',
    epic: 'coming_soon',
    snowflake: 'available',
    'aws-healthimaging': 'available',
    dicomweb: 'coming_soon',
} as const;

/**
 * Source type context - indicates which features use each source type
 * - 'source_registry': Configurable external data sources (in SourceRegistry)
 * - 'data_ingestion': Data ingestion workflows (in DataIngestion)
 * - 'both': Used in both features
 */
export type SourceTypeContext = 'source_registry' | 'data_ingestion' | 'both';

export const SOURCE_TYPE_CONTEXT: Record<SourceTypeBackend, SourceTypeContext> = {
    s3: 'both',
    http: 'both',
    sftp: 'both',
    healthlake: 'both',
    ehr: 'both',
    epic: 'both',
    snowflake: 'both',
    'aws-healthimaging': 'both',
    dicomweb: 'both',
    local_fs: 'data_ingestion', // Only for DataIngestion workflows, not SourceRegistry
} as const;

/**
 * Check if a source type is available for creation
 */
export function isSourceTypeAvailable(backendType: SourceTypeBackend): boolean {
    return SOURCE_TYPE_STATUS[backendType] === 'available';
}

/**
 * Check if a source type is coming soon
 */
export function isSourceTypeComingSoon(backendType: SourceTypeBackend): boolean {
    return SOURCE_TYPE_STATUS[backendType] === 'coming_soon';
}

/**
 * Check if a source type supports connection testing
 * Uses display name (e.g., 'S3', 'Snowflake', 'SFTP Connector')
 */
export function supportsConnectionTest(displayType: SourceTypeDisplay): boolean {
    const sourceType = Object.values(SOURCE_TYPES).find(type => type.display === displayType);
    return sourceType?.supportsConnectionTest ?? false;
}

/**
 * Get the display name for a source type
 * Accepts both backend and display names
 */
export function getSourceTypeDisplayName(sourceType: string): string {
    // If it's already a display name, return it
    if (displayToBackendMap[sourceType]) {
        return sourceType;
    }

    // If it's a backend name, map it to display name
    if (backendToDisplayMap[sourceType]) {
        return backendToDisplayMap[sourceType];
    }

    // Fallback: capitalize the source type
    return sourceType.charAt(0).toUpperCase() + sourceType.slice(1);
}

/**
 * Source type descriptions for UI display
 */
const SOURCE_TYPE_DESCRIPTIONS: Record<SourceTypeBackend, string> = {
    epic: 'Integration with Epic EHR systems',
    s3: 'Amazon S3 bucket integration',
    snowflake: 'Snowflake data warehouse connection',
    local_fs: 'Upload files directly from your local machine',
    http: 'Connect via HTTP endpoints',
    sftp: 'Secure SFTP data transfer',
    healthlake: 'AWS HealthLake data storage',
    ehr: 'Electronic Health Records integration',
    'aws-healthimaging': 'AWS HealthImaging DICOM storage',
    dicomweb: 'DICOM-web compliant medical imaging server',
};

/**
 * Source type colors for UI display
 */
const SOURCE_TYPE_COLORS: Record<SourceTypeBackend, string> = {
    epic: '#1f4e78',
    s3: '#569a31',
    snowflake: '#29b5e8',
    local_fs: '#6f42c1',
    http: '#FF8400',
    sftp: '#f28b20',
    healthlake: '#13c2c2',
    ehr: '#52c41a',
    'aws-healthimaging': '#FF9900',
    dicomweb: '#1890ff',
};

/**
 * Get source type array for UI components (dropdown, selection, etc.)
 * Returns array format compatible with legacy components
 */
export function getSourceTypesForDisplay() {
    return Object.values(SOURCE_TYPES).map(sourceType => ({
        value: sourceType.display,
        label: sourceType.display,
        icon:
            sourceType.icon.type === 'img'
                ? (sourceType.icon as any).component().props.src // Extract image path for img type
                : sourceType.icon.name,
        iconType: sourceType.icon.type,
        description: SOURCE_TYPE_DESCRIPTIONS[sourceType.backend],
        color: SOURCE_TYPE_COLORS[sourceType.backend],
    }));
}
